/**
 * @file スキャン画像 1 枚を Firebase Storage から取得して object URL にする。
 * @why: 画像は storage.rules (family member read) ゲートの平文オブジェクト。
 *   エクスポートと同じ getBytes 経路で取り、blob URL にして <img> に渡す。
 *   取得と URL の寿命は pageImageCache が持つ。表示中は acquire で参照を立て、
 *   unmount の release まで revoke されない (上限超えの同時表示でも URL が生きる)。
 *   取得済みの画像は path が変わったその描画で ready を返す。※なぜなら一度でも loading を
 *   返すと <img> が外れ、先読み済みのページ送りでも紙面が一瞬背景色になるためである。
 * @failure: 取得失敗は status "error" (詳細は console)。失敗はキャッシュに残らないので、
 *   同じページを開き直せば取り直す。
 */

import { useEffect, useState } from "react";
import { acquirePageImage, peekPageImage, releasePageImage } from "../lib/pageImageCache";

export type ScanImageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; url: string };

/** 取得結果をどの path のものかと対で持つ (path が変わった直後の古い結果を返さないため)。 */
type Settled = { path: string; state: ScanImageState };

export function useScanImage(imagePath: string | null, contentType: string): ScanImageState {
  const [settled, setSettled] = useState<Settled | null>(null);

  useEffect(() => {
    if (!imagePath) return;
    let cancelled = false;
    let acquired = false;
    // acquirePageImage は Firebase 初期化に失敗すると同期で throw しうる (env 不足の環境等)。
    // effect を巻き込んで落とさず、error 状態に倒す
    try {
      const promise = acquirePageImage(imagePath, contentType);
      acquired = true;
      promise
        .then((url) => {
          if (!cancelled) setSettled({ path: imagePath, state: { status: "ready", url } });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          console.error("[study] failed to load scan image", err);
          setSettled({ path: imagePath, state: { status: "error" } });
        });
    } catch (err) {
      console.error("[study] failed to load scan image", err);
      setSettled({ path: imagePath, state: { status: "error" } });
    }
    return () => {
      // revoke はキャッシュ側 (release で参照が切れて上限からあふれたとき)
      cancelled = true;
      if (acquired) releasePageImage(imagePath);
    };
  }, [imagePath, contentType]);

  if (!imagePath) return { status: "loading" };
  if (settled?.path === imagePath) return settled.state;
  const cached = peekPageImage(imagePath);
  return cached ? { status: "ready", url: cached } : { status: "loading" };
}
